import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { AuthpopupComponent } from '../components/authpopup/authpopup.component';
import { WebpopupdialogComponent } from '../../web/webpopupdialog/webpopupdialog.component';
import { SettingsService } from './settings.service';
import { FirebaseAuthService } from './firebase-auth.service';
import { MobileHelperService } from './mobile-helper.service';
import { UtilityFunctionsService } from './utility-functions.service';
import { SignInWithApple, SignInWithAppleOptions } from '@capacitor-community/apple-sign-in';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Toast } from '@capacitor/toast';
import { environment } from 'src/environments/environment';
import { Overlay } from '@angular/cdk/overlay';
import { POPUP_TEXT } from '../text/popup-texts';

@Injectable({
  providedIn: 'root'
})
export class AuthLandingPageService {
  private authDialogRef: MatDialogRef<any>;

  private _isMobileSplashVisible = new BehaviorSubject<boolean>(false);
  public isMobileSplashVisible$ = this._isMobileSplashVisible.asObservable();

  private _mobileSplashConfig = new BehaviorSubject<{ showSignInForm: boolean; redirectAfterAuth: boolean }>({
    showSignInForm: false,
    redirectAfterAuth: false
  });
  public mobileSplashConfig$ = this._mobileSplashConfig.asObservable();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private settingsService: SettingsService,
    private firebaseAuth: FirebaseAuthService,
    private settingService: SettingsService,
    private utilityService: UtilityFunctionsService,
    private overlay: Overlay
  ) {}

  openAuthModal(isSignIn: boolean = true, redirectAfterAuth: boolean = false): void {

    const isWebPlatform = environment.type === 'web';

    if (isWebPlatform) {
      // Check if user is on mobile device accessing web version
      const mobileDevice = this.detectMobileDevice();
      if (mobileDevice === 'android') {
        this.showMobileDeviceDialog(mobileDevice);
        return;
      } else if (mobileDevice === 'ios') {
        // For iOS, do nothing, as the user has a direct link to the App Store in the header.
        return;
      }

      console.log('AuthLandingPageService: Opening web auth popup');
      this.openWebAuthPopup(redirectAfterAuth);
    } else {
      console.log('AuthLandingPageService: Opening mobile splash screen');
      this.showMobileSplashScreen(redirectAfterAuth);
    }
  }

  private openWebAuthPopup(redirectAfterAuth: boolean = false): void {
    this.authDialogRef = this.dialog.open(AuthpopupComponent, {
      data: {},
      restoreFocus: false,
      panelClass: 'webpopup-overlay',
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });

    this.authDialogRef.afterClosed().subscribe(() => {
      this.authDialogRef = null;
    });

    if (redirectAfterAuth) {
      console.log('AuthLandingPageService: Setting up auth success subscription for web');
      this.subscribeToAuthSuccess();
    }
  }

  private showMobileSplashScreen(redirectAfterAuth: boolean = false): void {
    console.log('AuthLandingPageService: showMobileSplashScreen called');

    this._mobileSplashConfig.next({
      showSignInForm: true,
      redirectAfterAuth
    });

    this._isMobileSplashVisible.next(true);

    if (redirectAfterAuth) {
      console.log('AuthLandingPageService: Setting up auth success subscription for mobile');
      this.subscribeToAuthSuccess();
    }
  }


  async handleMobileAppleSignIn(): Promise<void> {
    try {
      if (this.settingsService.devicePlatform === 'ios') {
        const options: SignInWithAppleOptions = {
          clientId: environment.appId,
          redirectURI: null,
          scopes: 'email name',
          state: null,
          nonce: null,
        };
        const response = await SignInWithApple.authorize(options);
        await this.firebaseAuth.createFirebaseuser(response.response);
      } else {
        await this.firebaseAuth.loginWithAppleWeb();
      }

      this.closeAuthModal();
      setTimeout(() => {
        console.log('AuthLandingPageService: Navigating to / after Apple sign in');
        this.router.navigateByUrl('/');
      }, 300); // ⚠️ Increased delay to allow GSAP cleanup
    } catch (error) {
      this.utilityService.rollbarError('Apple sign in error: ' + error);
    }
  }

  async handleMobileGoogleSignIn(): Promise<void> {
    try {
      const googleUser = await GoogleAuth.signIn();
      await this.firebaseAuth.loginWithGoogleNative(googleUser.authentication.idToken);

      this.closeAuthModal();
      setTimeout(() => {
        console.log('AuthLandingPageService: Navigating to / after Google sign in');
        this.router.navigateByUrl('/');
      }, 300); // ⚠️ Increased delay to allow GSAP cleanup
    } catch (error) {
      this.utilityService.rollbarError('Google sign in error: ' + error);
      await Toast.show({
        text: error.toString(),
        duration: 'long',
        position: 'top'
      });
    }
  }

  closeAuthModal(): void {
    if (this.authDialogRef) {
      this.authDialogRef.close();
    }

    this._isMobileSplashVisible.next(false);
  }

  isAuthModalOpen(): boolean {
    return !!this.authDialogRef || this._isMobileSplashVisible.value;
  }

  private subscribeToAuthSuccess(): void {
    console.log('AuthLandingPageService: Starting auth success subscription');
    console.log('AuthLandingPageService: Auth success subscription setup complete');
  }

  onSplashSignInAppleClicked(): void {
    console.log('AuthLandingPageService: Apple sign in clicked from splash screen');
    this.handleMobileAppleSignIn();
  }

  // clicked from mobile build;
  onSplashSignInGoogleClicked(): void {
    console.log('AuthLandingPageService: Google sign in clicked from splash screen');
    if (this.settingService.devicePlatform === 'android') {
      this.handleMobileGoogleSignIn();
      return;
    } else if (this.settingService.devicePlatform === 'ios') {
      this.handleMobileGoogleSignIn();
      return;
    } else {
      // Fallback for MOBILE build, when native not accessible
      this.firebaseAuth.loginWithGoogle().then(async (token) => {
      }).catch(async (err) => {
        if (err.code === 'auth/user-disabled') {
          this.utilityService.showImportantErrorSnackBar('User disabled; Please,contact support', 0, 8000);
        } else {
          this.utilityService.showErrorSnackBar(err.code, 4000)
        }
        console.log(err);
      }).finally(() => {

      });
      return;
    }
  }

  onSplashCloseClicked(): void {
    console.log('AuthLandingPageService: Close splash screen clicked');
    this.closeAuthModal();
  }

  private detectMobileDevice(): 'android' | 'ios' | null {
    const userAgent = navigator.userAgent.toLowerCase();

    // Check for Android
    if (userAgent.includes('android')) {
      return 'android';
    }

    // Check for iOS
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      return 'ios';
    }

    return null;
  }

  private showMobileDeviceDialog(deviceType: 'android' | 'ios'): void {
    if (deviceType !== 'android') {
      // This part is no longer reachable, but kept for clarity.
      return;
    }

    const dialogRef = this.dialog.open(WebpopupdialogComponent, {
      data: this.utilityService.applyPopupText(POPUP_TEXT.WEB_DIALOG_ANDROID_SUPPORT_INFO),
      restoreFocus: false,
      panelClass: 'web-landing2025-page-popup',
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log(`AuthLandingPageService: ${deviceType} device dialog closed`);
    });
  }
}
